import { css, cx } from '@emotion/css';
import { useNavigator } from '../../routing/navigator';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { Colors } from '../../shared/styles/colors';
import {
  h1Style,
  h3Style,
  tabularNumbersStyle,
  valueStyle,
} from '../../shared/styles/text';
import { PaymentTypeEnum } from '../../shared/types/enums';
import { Account } from '../../shared/types/server-types';
import { useActiveAuthorization } from '../../shared/services/api/payment-authorization';
import { Radius, Shadow } from '../../shared/styles/tokens';

interface AccountCardProps {
  account: Account;
}

export const AccountCard = (props: AccountCardProps): JSX.Element => {
  const navigator = useNavigator();
  const { account } = props;

  // Card reservations (preauthorization) are per location and independent of
  // the wallet balance; the endpoint also says whether the location offers them.
  const { data: authInfo } = useActiveAuthorization(account.location.id);
  const activeAuthorization = authInfo?.authorization ?? null;
  const authorizationRemaining = activeAuthorization
    ? Math.max(
        activeAuthorization.reservedAmount -
          (activeAuthorization.consumedAmount ?? 0),
        0,
      )
    : 0;

  const showActions =
    account.location.hasCardPaymentEnabled &&
    (account.paymentType === PaymentTypeEnum.PREPAID ||
      (authInfo?.preauthEnabled && !activeAuthorization));

  return (
    <div>
      <div
        className={cx(
          cardOuterRootStyle(account.paymentType),
          showActions && cardWithActionsStyle,
        )}
      >
        <div className={cardToolbarStyle}>
          <h3 className={cx(h3Style, locationTextStyle, whiteTextStyle)}>
            {account.location.name}
          </h3>
          {account.location.subLocations &&
            account.location.subLocations.length > 0 && (
              <div className={subLocationTextStyle}>
                {`(`}
                {account.location.subLocations.map((subLocation, index) => (
                  <span key={subLocation.id}>
                    {subLocation.name}
                    {account.location.subLocations &&
                      index < account.location.subLocations.length - 1 &&
                      ', '}
                  </span>
                ))}
                {`)`}
              </div>
            )}
        </div>

        {account.paymentType === PaymentTypeEnum.PREPAID ? (
          <div>
            <p className={cx(valueStyle, mutedWhiteTextStyle)}>
              Available funds
            </p>
            <h1 className={cx(h1Style, whiteTextStyle, amountStyle)}>
              {account.balance.toFixed(2)} EUR
            </h1>
          </div>
        ) : (
          <h1 className={accountTypeTextStyle}>{account.paymentType}</h1>
        )}

        {activeAuthorization && (
          <div className={reservationInfoStyle}>
            <p className={cx(valueStyle, mutedWhiteTextStyle)}>
              Reserved on card
            </p>
            <h3 className={cx(h3Style, whiteTextStyle, tabularNumbersStyle)}>
              {`${authorizationRemaining.toFixed(2)} EUR left of ${activeAuthorization.reservedAmount.toFixed(2)} EUR`}
            </h3>
            <p
              className={cx(
                valueStyle,
                mutedWhiteTextStyle,
                reservationHintStyle,
              )}
            >
              You are charged only for what your sessions actually use.
            </p>
          </div>
        )}
      </div>
      {showActions && (
        <div className={cardActionRootStyle}>
          {account.paymentType === PaymentTypeEnum.PREPAID && (
            <Button
              variant={ButtonVariantEnum.FILLED}
              size={ButtonSizeEnum.MEDIUM}
              text={`add funds`}
              fullWidth
              onClick={() =>
                navigator.toWalletAddFundsToLocation(account.location.id)
              }
            />
          )}
          {authInfo?.preauthEnabled && !activeAuthorization && (
            <Button
              variant={ButtonVariantEnum.OUTLINE}
              size={ButtonSizeEnum.MEDIUM}
              text={`reserve funds`}
              fullWidth
              onClick={() => navigator.toReserveFunds(account.location.id)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const cardActionRootStyle = css`
  display: flex;
  width: 100%;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid ${Colors.BORDER};
  border-top: 0;
  border-bottom-left-radius: ${Radius.xl};
  border-bottom-right-radius: ${Radius.xl};
  background: ${Colors.WHITE};

  > button {
    min-width: 0;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    font-size: 0.9375rem;
    box-shadow: none;
  }
`;

const reservationInfoStyle = css`
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
`;

const reservationHintStyle = css`
  margin-top: 0.25rem;
`;

const cardToolbarStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1.25rem;
`;

const whiteTextStyle = css`
  color: ${Colors.WHITE};
`;

const mutedWhiteTextStyle = css`
  color: rgba(255, 255, 255, 0.82);
`;

const amountStyle = css`
  ${tabularNumbersStyle}
  margin-top: 0.125rem;
`;

const locationTextStyle = css`
  max-width: 80%;
  overflow-wrap: anywhere;
`;

const subLocationTextStyle = css`
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.9rem;
  margin-top: 0.3rem;
`;

const accountTypeTextStyle = css`
  ${h1Style}
  text-transform: lowercase;
  &:first-letter {
    text-transform: uppercase;
  }
  color: ${Colors.WHITE};
  margin-top: 2.5rem;
`;

const cardOuterRootStyle = (paymentType: PaymentTypeEnum) => css`
  position: relative;
  padding: 1.25rem;
  border-radius: ${Radius.xl};
  box-shadow: ${Shadow.card};
  overflow: hidden;
  /* Prepaid stays in the gray family, everything else in brand blue —
     the same distinction as before, with readable text. */
  background: ${paymentType === PaymentTypeEnum.PREPAID
    ? `linear-gradient(135deg, #5c6f80 0%, #3a4a58 100%)`
    : `linear-gradient(135deg, ${Colors.CAPRI_BLUE} 0%, ${Colors.NAVY} 100%)`};
`;

const cardWithActionsStyle = css`
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;
